import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';

export const useOrders = () => {
    const [loading, setLoading] = useState(true);
    const [customers, setCustomers] = useState([]);
    const [summary, setSummary] = useState({ total_customers: 0, active_customers: 0, total_value: 0, total_items: 0 });
    const [activePallet, setActivePallet] = useState(null);
    const [categories, setCategories] = useState([]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [customersRes, summaryRes, activePalletRes, categoriesRes] = await Promise.all([
                api.get('/orders/v2/customers'),
                api.get('/orders/v2/summary'),
                api.get('/pallets/active'),
                api.get('/categories')
            ]);
            setCustomers(customersRes.data || []);
            setSummary(summaryRes.data || { total_customers: 0, active_customers: 0, total_value: 0, total_items: 0 });
            setActivePallet(activePalletRes.data || null);
            setCategories(categoriesRes.data || []);
        } catch (err) {
            console.error('Error loading data:', err);
            toast.error('Eroare la încărcarea datelor');
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Toggle informed status
    const toggleInformed = async (customerId) => {
        try {
            const res = await api.put(`/orders/v2/customers/${customerId}/informed`);
            toast.success(res.data.informed ? 'Client informat!' : 'Marcat ca neinformat');
            loadData();
            return res.data;
        } catch (err) {
            toast.error('Eroare la actualizare');
            throw err;
        }
    };

    // Update customer info
    const updateCustomer = async (customerId, data) => {
        try {
            await api.put(`/orders/v2/customers/${encodeURIComponent(customerId)}`, data);
            toast.success('Client actualizat');
            loadData();
        } catch (err) {
            toast.error('Eroare la actualizare client');
            throw err;
        }
    };

    // Delete customer
    const deleteCustomer = async (customerId) => {
        try {
            await api.delete(`/orders/v2/customers/${customerId}`);
            toast.success('Client șters');
            loadData();
        } catch (err) {
            toast.error('Eroare la ștergere');
            throw err;
        }
    };

    // Get customer items
    const getCustomerItems = async (customerId) => {
        try {
            const res = await api.get(`/orders/v2/customers/${customerId}/items`);
            return res.data || [];
        } catch (err) {
            toast.error('Eroare la încărcarea comenzii');
            throw err;
        }
    };

    // Update item
    const updateItem = async (itemId, data) => {
        try {
            const res = await api.put(`/orders/v2/items/${itemId}`, data);
            toast.success('Produs actualizat');
            // Show stock transparency message
            if (res.data?.stock_message) {
                toast.info(res.data.stock_message, { duration: 4000 });
            }
            loadData();
        } catch (err) {
            toast.error('Eroare la actualizare');
            throw err;
        }
    };

    // Delete item
    const deleteItem = async (itemId, returnToStore = true) => {
        try {
            const res = await api.delete(`/orders/v2/items/${itemId}?return_to_store=${returnToStore}`);
            toast.success('Produs șters');
            // Show stock transparency message
            if (res.data?.stock_message) {
                toast.info(res.data.stock_message, { duration: 4000 });
            }
            loadData();
        } catch (err) {
            toast.error('Eroare la ștergere');
            throw err;
        }
    };

    // Search product by barcode
    const searchProduct = async (barcode) => {
        try {
            const res = await api.get(`/orders/v2/search-product/${barcode}`);
            return res.data;
        } catch (err) {
            toast.error('Eroare la căutarea produsului');
            throw err;
        }
    };

    // Add product from stock to customer
    const addFromStock = async (customerId, data) => {
        try {
            const res = await api.post(`/orders/v2/customers/${customerId}/add-from-stock`, data);
            return res.data;
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la adăugare');
            throw err;
        }
    };

    // Register and sell from pallet
    const registerAndSell = async (data) => {
        try {
            const res = await api.post('/orders/v2/register-and-sell', data);
            // Return response data for caller to check if taken from store
            loadData();
            return res.data;
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la înregistrare');
            throw err;
        }
    };

    // Complete order with verification
    const completeVerified = async (customerId, verificationData, actionForRest, includeTransport) => {
        try {
            await api.post(`/orders/v2/customers/${customerId}/complete-verified`, {
                verification_data: verificationData,
                action_for_rest: actionForRest,
                include_transport: includeTransport
            });
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la finalizare');
            throw err;
        }
    };

    return {
        loading,
        customers,
        summary,
        activePallet,
        categories,
        loadData,
        toggleInformed,
        updateCustomer,
        deleteCustomer,
        getCustomerItems,
        updateItem,
        deleteItem,
        searchProduct,
        addFromStock,
        registerAndSell,
        completeVerified
    };
};

export default useOrders;
